import { parseISO, parseJSON } from "date-fns";
import { keepPreviousData } from "@tanstack/react-query";
import {
  HistoricQuotesIntervalEnum,
  ShowHistoricQuoteIntervalEnum,
  useHistoricQuotes,
  useIntraday,
  useListAndSearchAllBookings,
  useShowHistoricQuote,
} from "@bn/api/apiv1";
import { HistoricQuote } from "@api";
import { addAreaValues, getInterval } from "@helpers";
import { mergeCloseByDate } from "@helpers/charts";
import { useDividends } from "@api/v1/hooks/useDividends";
import { BOOKINGS_COMPATIBILITY_VERSION, QUOTE_PROVIDERS } from "@constants";
import { mergeQuotesByDate } from "../utils/mergeQuotes";

interface Attrs {
  investmentId?: number;
  startDate?: Date;
  endDate?: Date;
  tickerSymbol?: string;
  tickerSymbolWithCurrency?: string;
  comparedTickerSymbol?: string;
  quoteProvider?: string;
  currentInterval?: IntervalType;
  currency?: string;
}

interface Options {
  showEvents: boolean;
}

export const useChartData = (
  {
    investmentId,
    currentInterval,
    startDate,
    endDate,
    tickerSymbol,
    tickerSymbolWithCurrency,
    comparedTickerSymbol,
    quoteProvider,
    currency,
  }: Attrs,
  options: Options = { showEvents: false }
) => {
  const interval = getInterval(startDate, endDate);

  const dividendsQuery = useDividends(
    {
      investmentId: investmentId ? [investmentId] : undefined,
      excludeImported: false,
      taxRate: 0,
    },
    { staleTime: Infinity, enabled: options.showEvents && !!investmentId }
  );

  const dividends =
    dividendsQuery.data?.dividends?.map(({ date, dividendPerShare }) => ({
      date: parseJSON(date!),
      dividendPerShare: dividendPerShare!,
    })) || [];

  const bookingsQuery = useListAndSearchAllBookings(
    {
      investmentId: investmentId ? [investmentId] : undefined,
      enumCompatibilityVersion: BOOKINGS_COMPATIBILITY_VERSION,
    },
    { staleTime: Infinity, enabled: options.showEvents && !!investmentId }
  );

  const bookings =
    bookingsQuery.data?.bookings?.map(({ valueDate, amount, type }) => ({
      valueDate: parseJSON(valueDate!),
      amount,
      type,
    })) || [];

  const {
    data: historicQuoteData,
    isFetching: isLoadingHistoricQuote,
    isPending: isPendingHistoricQuote,
  } = useHistoricQuotes(
    {
      investmentId: investmentId ? [investmentId] : undefined,
      tickerSymbol: tickerSymbolWithCurrency
        ? [tickerSymbolWithCurrency]
        : undefined,
      interval: interval as HistoricQuotesIntervalEnum,
      from: startDate?.toISOString(),
      to: endDate?.toISOString(),
      convertToCurrency: currency,
    },
    {
      staleTime: Infinity,
      // No investmentId when charting a security that isn't held — the ticker
      // alone is enough for the quotes endpoint.
      enabled: !!tickerSymbolWithCurrency,
      placeholderData: keepPreviousData,
    }
  );
  const historicQuote = ((tickerSymbolWithCurrency &&
    historicQuoteData?.historicQuotes?.[tickerSymbolWithCurrency]) ||
    []) as HistoricQuote[];

  const {
    data: comparisonHistoricQuotesData,
    isFetching: isFetchingHistoricQuote,
  } = useShowHistoricQuote(
    {
      quoteProvider: QUOTE_PROVIDERS.ariva,
      tickerSymbol: comparedTickerSymbol ?? "",
      interval: interval as ShowHistoricQuoteIntervalEnum, //TODO: Remove once swagger is updated to show day interval
      from: startDate?.toISOString(),
      to: endDate?.toISOString(),
      convertToCurrency: currency,
    },
    {
      staleTime: Infinity,
      enabled: Boolean(comparedTickerSymbol),
      placeholderData: keepPreviousData,
    }
  );
  const comparisonHistoricQuotes =
    (comparisonHistoricQuotesData?.historicQuotes ?? []) as HistoricQuote[];

  const shouldRequestIntradayQuotes = currentInterval === "oneDay";
  const isIntradayEnabled =
    !!quoteProvider && !!tickerSymbol && shouldRequestIntradayQuotes;
  const isComparisonIntradayEnabled =
    Boolean(comparedTickerSymbol) && shouldRequestIntradayQuotes;

  const {
    data: intraday,
    isFetching: isFetchingIntraday,
    isPending: isPendingIntraday,
  } = useIntraday(
    {
      tickerSymbol: tickerSymbol!,
      quoteProvider: quoteProvider!,
      period: "intraday10",
      from: startDate?.toISOString()!,
      to: endDate?.toISOString()!,
      convertToCurrency: currency,
    },
    {
      enabled: isIntradayEnabled,
    }
  );

  const {
    data: comparisonIntraday,
    isFetching: isFetchingComparisonIntraday,
    isPending: isPendingComparisonIntraday,
  } = useIntraday(
    {
      tickerSymbol: comparedTickerSymbol!,
      quoteProvider: QUOTE_PROVIDERS.ariva,
      period: "intraday10",
      from: startDate?.toISOString()!,
      to: endDate?.toISOString()!,
      convertToCurrency: currency,
    },
    {
      enabled: isComparisonIntradayEnabled,
    }
  );

  const preparedIntradayData: HistoricQuote[] =
    intraday?.quoteBars?.map(({ dateTime, close }) => ({
      date: dateTime!,
      value: close!,
    })) ?? [];

  const preparedComparisonIntraday: HistoricQuote[] =
    comparisonIntraday?.quoteBars?.map(({ dateTime, close }) => ({
      date: dateTime!,
      value: close!,
    })) ?? [];

  const mainQuotes = shouldRequestIntradayQuotes
    ? preparedIntradayData
    : historicQuote;
  const comparisonQuotes = shouldRequestIntradayQuotes
    ? preparedComparisonIntraday
    : comparisonHistoricQuotes;

  const showComparisonQuotes =
    comparedTickerSymbol && comparisonQuotes.length > 0;

  const chartData = showComparisonQuotes
    ? mergeQuotesByDate(mainQuotes, comparisonQuotes)
    : mainQuotes;

  const mainChartData = addAreaValues(
    chartData.map(({ date, ...rest }) => ({
      ...rest,
      date: parseISO(date.slice(0, -5)),
    })),
    "value"
  );

  const data = mergeCloseByDate(mainChartData, dividends || [], bookings || []);

  return {
    data,
    isFetching: shouldRequestIntradayQuotes
      ? (isIntradayEnabled ? isFetchingIntraday : false) ||
        (isComparisonIntradayEnabled ? isFetchingComparisonIntraday : false)
      : isLoadingHistoricQuote || isFetchingHistoricQuote,
    isPending: shouldRequestIntradayQuotes
      ? (isIntradayEnabled ? isPendingIntraday : false) ||
        (isComparisonIntradayEnabled ? isPendingComparisonIntraday : false)
      : isPendingHistoricQuote,
  };
};
